import { FactorGroup } from './types';

export const frequencyValues = [5, 20, 50, 100, 150, 220, 300, 500, 750, 1000, 1500, 2000, 2500];

export const frequencyMarks = {
	0: '5',
	1: '20',
	2: '50',
	3: '100',
	4: '150',
	5: '220',
	6: '300',
	7: '500',
	8: '750',
	9: '1000',
	10: '1500',
	11: '2000',
	12: '2500'
};

export const KIM_LHC_BODY_POSTURE = {
	option_1: 0,
	option_2: 3,
	option_3: 5,
	option_4: 7,
	option_5: 9,
	option_6: 10,
	option_7: 13,
	option_8: 15,
	option_9: 18,
	option_10: 20
} as const;

export const KIM_LHC_POSTURE_ADDITIONAL_FACTOR_TWIST = {
	twist_occasional_trunk: 1,
	twist_frequent_trunk: 3
} as const;

export const KIM_LHC_POSTURE_ADDITIONAL_FACTOR_LOAD_CENTER = {
	load_center_occasional: 1,
	load_center_frequent: 3
} as const;

export const KIM_LHC_POSTURE_ADDITIONAL_FACTOR_ARMS = {
	arms_occasional: 0.5,
	arms_frequent: 1
} as const;

export const KIM_LHC_POSTURE_ADDITIONAL_FACTOR_HANDS = {
	hands_occasional: 1,
	hands_frequent: 2
} as const;

export const ADDITIONAL_FACTOR_GROUPS: FactorGroup[] = [
	{
		name: 'posture_additional_factor_twist',
		options: [
			{
				value: 'twist_occasional_trunk',
				labelKey: 'Occasional identifiable trunk twisting and/or lateral bending'
			},
			{
				value: 'twist_frequent_trunk',
				labelKey: 'Frequent or constant identifiable trunk twisting and/or lateral bending'
			}
		]
	},
	{
		name: 'posture_additional_factor_load_center',
		options: [
			{
				value: 'load_center_occasional',
				labelKey: 'Load center and/or hands occasionally away from the body'
			},
			{
				value: 'load_center_frequent',
				labelKey: 'Load center and/or hands frequently or constantly away from the body'
			}
		]
	},
	{
		name: 'posture_additional_factor_arms',
		options: [
			{
				value: 'arms_occasional',
				labelKey: 'Arms occasionally elevated, with hands between elbow and shoulder level'
			},
			{
				value: 'arms_frequent',
				labelKey: 'Arms frequently or constantly elevated, with hands between elbow and shoulder level'
			}
		]
	},
	{
		name: 'posture_additional_factor_hands',
		options: [
			{
				value: 'hands_occasional',
				labelKey: 'Hands occasionally above shoulder height'
			},
			{
				value: 'hands_frequent',
				labelKey: 'Hands frequently or constantly above shoulder height'
			}
		]
	}
];

export const WORK_CONDITION_OPTIONS = {
	hands_position: [
		{
			value: 'hands_position_occasional',
			resultTitleKey: 'Hand/arm position and movements occasional',
			descriptionKey: 'Occasionally at the limit of range of motion',
			labelKey: 'Occasionally at the limit of range of motion'
		},
		{
			value: 'hands_position_frequent',
			resultTitleKey: 'Hand/arm position and movements frequent',
			descriptionKey: 'Frequently/constantly at the limit of range of motion',
			labelKey: 'Frequently/constantly at the limit of range of motion'
		}
	],
	transfer: [
		{
			value: 'transfer_occasional',
			titleKey: 'Restricted',
			resultTitleKey: 'Force transfer/application restricted',
			descriptionKey:
				'Loads difficult to hold / greater holding force required / absence of molded handles / use of work gloves'
		},
		{
			value: 'transfer_frequent',
			titleKey: 'Considerably hindered',
			resultTitleKey: 'Force transfer/application considerably hindered',
			descriptionKey:
				'Loads almost impossible to hold / slippery, soft or sharp edges surfaces / absence or inadequacy of grips / use of work gloves'
		}
	],
	adverse_conditions: [
		{
			value: 'climatical_conditions_unfavourable',
			resultTitleKey: 'Adverse environmental conditions unfavorable climatic',
			descriptionKey: 'Unfavorable climatic conditions and/or physical loads caused by heat, wind, cold or humidity',
			labelKey: 'Unfavorable climatic conditions and/or physical loads caused by heat, wind, cold or humidity'
		}
	],
	spatial_condition: [
		{
			value: 'spatial_condition_occasional',
			titleKey: 'Restricted',
			resultTitleKey: 'Spatial conditions restricted',
			descriptionKey: 'Work area less than 1.5 m², moderately dirty floor and slightly uneven floor, slight slope up to 5°, slightly restricted stability, load must be positioned with precision'
		},
		{
			value: 'spatial_condition_frequent',
			titleKey: 'Unfavorable',
			resultTitleKey: 'Spatial conditions unfavorable',
			descriptionKey:
				'Significantly restricted freedom of movement or insufficient space for movement, work in confined spaces, very dirty, uneven or roughly paved floor, steps/holes, steep slope of 5–10°, restricted stability, load must be positioned with great precision'
		}
	],
	clothes: [
		{
			value: 'clothes_additional_effort',
			resultTitleKey: 'Clothes',
			labelKey:
				'Additional physical effort due to heavy clothing or equipment that restricts movement (such as, heavy rain protection clothing, full protective clothing, breathing equipment, tool belts or similar)',
			descriptionKey:
				'Additional physical effort due to heavy clothing or equipment that restricts movement (such as, heavy rain protection clothing, full protective clothing, breathing equipment, tool belts or similar)'
		}
	],
	hold_load: [
		{
			value: 'hold_load_difficult',
			titleKey: 'Difficulty',
			resultTitleKey: 'Hold/carry difficulty',
			descriptionKey: 'The load must be held for more than 5 to 10 seconds or carried to a distance of more than 2 m to 5 m'
		},
		{
			value: 'hold_load_significant_difficulty',
			titleKey: 'Significant difficulty',
			resultTitleKey: 'Hold/carry significant difficulty',
			descriptionKey: 'The load must be held for more than 10 seconds or carried to a distance of more than 5 m'
		}
	]
} as const;

export const POSTURE_BODY_DESCRIPTION =
	'Select the option that represents the posture at the start and end of the operation';

export const POSTURE_BODY_TOOLTIP =
	'The movement can occur in both directions; postures may indicate the beginning or end of the operation. When there are multiple postures in the same option, they all represent the same condition.';

export const WORK_ORGANIZATION_OPTIONS = {
	temporal_distribution: [
		{
			value: 'good',
			titleKey: 'Good',
			descriptionKey:
				'Frequent variation of the physical load situation due to other activities (including other types of physical load) / without a rigid sequence of heavy physical loads within a single type of physical load during a single working day'
		},
		{
			value: 'restricted',
			titleKey: 'Restricted',
			descriptionKey:
				'Rare variation of the physical load situation due to other activities (including other types of physical load) / occasional sequence of heavy physical loads within a single type of physical load during a single working day'
		},
		{
			value: 'desfavored',
			titleKey: 'Unfavorable',
			descriptionKey:
				'No or almost no variation of the physical load situation due to other activities (including other types of physical load) / frequent sequence of heavy physical loads within a single type of physical load during a single working day, with simultaneous load peaks'
		}
	]
} as const;
